import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilLine, Tray, DeviceMobile, CalendarBlank } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge, PlatformChip } from "../lib/meta";
import { Explainer } from "../components/friendly";

export default function Dashboard() {
  const { user, org, activeClient, activeClientId } = useApp();
  const [drafts, setDrafts] = useState([]);
  const [audit, setAudit] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeClientId) return;
    api.get(`/drafts?client_id=${activeClientId}`).then((r) => setDrafts(r.data));
    api.get("/audit").then((r) => setAudit(r.data.slice(0, 8)));
  }, [activeClientId]);

  const count = (s) => drafts.filter((d) => d.status === s).length;
  const stats = [
    { label: "In review", value: count("pending_approval"), to: "/approvals", icon: Tray },
    { label: "Ready to post", value: count("approved"), to: "/queue", icon: DeviceMobile },
    { label: "Drafts", value: count("draft") + count("changes_requested"), to: "/composer", icon: PencilLine },
    { label: "Posted", value: count("posted"), to: "/calendar", icon: CalendarBlank },
  ];

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium">{org?.name}</p>
        <h1 className="font-display font-bold text-3xl tracking-tight mt-1">Good to see you, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-slate-500 mt-1">Working on <b>{activeClient?.name || "—"}</b>. Here's where things stand.</p>
      </div>
      <Explainer id="dashboard">Your home base. The four numbers show where every post stands — drafted, waiting for review, ready to post, and already published. Click any card to jump straight in.</Explainer>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, to, icon: Icon }) => (
          <button key={label} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`} onClick={() => navigate(to)}
            className="text-left bg-white border border-slate-200/80 rounded-xl p-5 hover-lift card-float">
            <Icon size={20} weight="duotone" className="text-[hsl(9,69%,62%)]" />
            <p className="font-display font-bold text-3xl mt-3">{value}</p>
            <p className="text-[12px] text-slate-500 mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-slate-200/80 rounded-xl card-float">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-bold text-base">Recent content</h2>
            <button data-testid="dashboard-new-draft" onClick={() => navigate("/composer")} className="text-[12px] font-semibold text-[hsl(9,55%,48%)] hover:underline">+ New draft</button>
          </div>
          <div className="divide-y divide-slate-100">
            {drafts.slice(0, 6).map((d) => (
              <div key={d.draft_id} className="px-5 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-slate-700 truncate">{d.caption}</p>
                  <div className="flex items-center gap-3 mt-1"><PlatformChip platform={d.platform} />{d.scheduled_date && <span className="text-[11px] text-slate-400">{d.scheduled_date}</span>}</div>
                </div>
                <StatusBadge status={d.status} />
              </div>
            ))}
            {!drafts.length && <p className="px-5 py-8 text-sm text-slate-400">No content yet — open the Composer to create your first draft.</p>}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200/80 rounded-xl card-float">
          <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-base">Activity</h2></div>
          <div className="divide-y divide-slate-100">
            {audit.map((a) => (
              <div key={a.log_id} className="px-5 py-3">
                <p className="text-[13px] text-slate-700"><b>{a.user_name}</b> · {a.action.replace(/_/g, " ")}</p>
                {a.detail && <p className="text-[12px] text-slate-400 truncate">{a.detail}</p>}
              </div>
            ))}
            {!audit.length && <p className="px-5 py-8 text-sm text-slate-400">No activity yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
